/**
 * Global search surface — desktop input + dropdown panel, mobile full-screen
 * overlay (triggered from MobileSearchButton). Wires `useGlobalSearch` state
 * to the row renderer and the result-type → route mapping.
 */

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "@/contexts/ThemeContext";
import { Search, X } from "lucide-react";
import { useGlobalSearch } from "./useGlobalSearch";
import { getTypeColor, getTypeIcon, searchResultRoute, TYPE_ORDER } from "./searchHelpers";
import SearchResultRow from "./SearchResultRow";

// Reset iOS viewport zoom after keyboard dismissal. iOS zooms in when an
// input is focused and doesn't always reset. Toggling maximum-scale on the
// viewport meta forces a zoom reset.
function resetViewportZoom() {
  const meta = document.querySelector('meta[name="viewport"]');
  if (!meta) return;
  const original = meta.getAttribute("content");
  meta.setAttribute("content", original + ", maximum-scale=1");
  requestAnimationFrame(() => {
    meta.setAttribute("content", original);
  });
}

export default function GlobalSearch({ showMobileButton }) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { searchQuery, setSearchQuery, searchResults, searching, groupedResults, resetSearch } =
    useGlobalSearch();
  const [showSearch, setShowSearch] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const searchRef = useRef(null);
  const mobileSearchRef = useRef(null);

  const getTypeLabel = (type) => t(`searchTypes.${type}`, { defaultValue: type });

  // Close search dropdown when clicking outside.
  useEffect(() => {
    function handleClickOutside(event) {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearch(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const closeMobileSearch = () => {
    setMobileSearchOpen(false);
    resetSearch();
    setShowSearch(false);
    resetViewportZoom();
  };

  const handleResultClick = (result) => {
    setShowSearch(false);
    resetSearch();
    const target = searchResultRoute(result.type);
    if (target) navigate(target);
  };

  return (
    <>
      {/* Mobile full-screen search overlay */}
      {mobileSearchOpen && (
        <div
          className={`sm:hidden fixed inset-0 z-[60] flex flex-col ${theme === "light" ? "bg-[#f6f8fc]" : "bg-[#0f172a]"}`}
          style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
        >
          <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-800">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                ref={mobileSearchRef}
                autoFocus
                type="text"
                placeholder={t("search.placeholder")}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowSearch(true);
                }}
                className="w-full bg-[#1e293b] border border-slate-700 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-slate-400 focus:border-altiv-navy focus:outline-none"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <button
              onClick={closeMobileSearch}
              className="text-slate-400 font-medium text-sm px-1 py-2"
            >
              {t("search.cancel")}
            </button>
          </div>

          {/* Mobile search results */}
          <div className="flex-1 overflow-y-auto">
            {searching ? (
              <div className="flex items-center justify-center gap-2 p-8 text-slate-400 text-sm">
                <div className="w-4 h-4 border-2 border-slate-500 border-t-altiv-navy rounded-full animate-spin" />
                {t("search.searching")}
              </div>
            ) : searchQuery.length >= 2 && searchResults.length === 0 ? (
              <p className="text-center text-slate-400 text-sm p-8">
                {t("search.noResults")} &quot;<span className="text-white">{searchQuery}</span>
                &quot;
              </p>
            ) : searchResults.length > 0 ? (
              <div>
                {TYPE_ORDER.map((type) => {
                  const results = groupedResults[type];
                  if (!results || results.length === 0) return null;
                  return (
                    <div key={type}>
                      <div className="px-4 py-2 bg-slate-800/50 text-xs font-bold text-slate-400 uppercase tracking-wider">
                        {getTypeLabel(type)}s ({results.length})
                      </div>
                      {results.map((result) => (
                        <SearchResultRow
                          key={result.id}
                          result={result}
                          query={searchQuery}
                          variant="mobile"
                          onClick={() => {
                            handleResultClick(result);
                            closeMobileSearch();
                          }}
                        />
                      ))}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-6 text-center text-slate-400 text-sm">
                <Search className="w-8 h-8 mx-auto mb-2 text-slate-700" />
                {t("search.across")}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Search - Desktop/tablet */}
      <div ref={searchRef} className="relative hidden sm:block">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            data-testid="global-search"
            type="text"
            placeholder={t("search.placeholderDesktop")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setShowSearch(true)}
            className="w-48 lg:w-72 bg-[#1e293b] border border-slate-700 rounded-xl pl-10 pr-4 py-2 text-sm text-white placeholder:text-slate-400 focus:border-altiv-navy focus:ring-1 focus:ring-altiv-navy focus:outline-none transition-all duration-200"
          />
          {searchQuery && (
            <button
              onClick={() => {
                resetSearch();
                setShowSearch(false);
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Search Dropdown — always in DOM, opacity-controlled to avoid mount flash */}
        <div
          className={`absolute right-0 top-full mt-2 w-[420px] bg-[#1e293b] border border-slate-700 rounded-xl shadow-2xl overflow-hidden z-50 transition-opacity duration-150
            ${showSearch && searchQuery.length >= 2 ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        >
          {searching ? (
            <div className="p-4 text-center text-sm text-slate-400">
              <div className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-slate-500 border-t-[#0052FF] rounded-full animate-spin"></div>
                {t("search.searching")}
              </div>
            </div>
          ) : searchResults.length === 0 ? (
            <div className="p-4 text-center text-sm text-slate-400">
              {searchQuery.length >= 2 ? (
                <>
                  {t("search.noResults")} &quot;
                  <span className="text-white">{searchQuery}</span>&quot;
                </>
              ) : null}
            </div>
          ) : (
            <div className="max-h-[70vh] overflow-y-auto">
              <div className="px-4 py-2 bg-slate-800/30 border-b border-slate-800 flex items-center justify-between">
                <span className="text-xs font-medium text-slate-400">
                  {searchResults.length}{" "}
                  {searchResults.length !== 1 ? t("search.results") : t("search.result")}{" "}
                  {t("search.forQuery")} &quot;<span className="text-white">{searchQuery}</span>
                  &quot;
                </span>
                <span className="text-[10px] text-slate-400">{t("search.bestMatches")}</span>
              </div>
              {TYPE_ORDER.map((type) => {
                const results = groupedResults[type];
                if (!results || results.length === 0) return null;
                return (
                  <div key={type}>
                    <div className="px-4 py-2 bg-slate-800/50 text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
                      <span className={`p-1 rounded ${getTypeColor(type)}`}>
                        {getTypeIcon(type)}
                      </span>
                      {getTypeLabel(type)}s ({results.length})
                    </div>
                    {results.map((result) => (
                      <SearchResultRow
                        key={result.id}
                        result={result}
                        query={searchQuery}
                        variant="desktop"
                        onClick={() => handleResultClick(result)}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Mobile search trigger — hidden for owners (stats take the space) */}
      {showMobileButton && (
        <button
          className="sm:hidden p-2.5 text-slate-400 active:text-white active:scale-95 active:bg-slate-800/50 rounded-xl transition-all"
          onClick={() => setMobileSearchOpen(true)}
          aria-label={t("search.placeholder")}
        >
          <Search className="w-5 h-5" />
        </button>
      )}
    </>
  );
}
